import React, { FC, useEffect, useState } from 'react';
import {
  Modal,
  Paper,
  Box,
  IconButton,
  Fade,
  Backdrop,
  CircularProgress,
  Tabs,
  Tab,
  Typography,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Chip,
  Link,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SwapCallsIcon from '@mui/icons-material/SwapCalls';
import WatchIcon from '@mui/icons-material/Watch';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { format, addSeconds } from 'date-fns';
import dayjs from 'dayjs';
import TrainingModalHeader from './components/training-modal-header';
import TrainingModalSummary from './components/training-modal-summary';
import useService from '../../hooks/service.hook';
import TrainingsService from '../../services/trainings.service';
import AssignedTrainingsService from '../../services/assigned-trainings.service';
import CustomTrainingFormsService from '../../services/custom-training-forms.service';
import TeamsService from '../../services/teams.service';
import { Training, CustomTrainingForm } from '../../models/training.models';
import DescriptionTab from './tabs/description.tab';
import AnalyticsTab from './tabs/analytics.tab';
import MembersTab from './tabs/members.tab';
import LogTab from './tabs/log.tab';
import CommentsTab from './tabs/comments.tab';
import FormsTab from './tabs/forms.tab';
import EquipmentTab from './tabs/equipment.tab';
import COEEditPersonalTrainingModal from '../coe-edit-personal-training-modal.component';
import TransferWorkoutModal from '../coe-training-modal/TransferWorkoutModal';
import EditTrainingComponent from '../training-sideviews/edit-training/edit-training.component';
import { palette } from '../../theme';

interface TrainingModalV2Props {
  open: boolean;
  onClose: () => void;
  trainingId: number;
  trainingType: 'athlete' | 'coach' | 'guardian';
  onTrainingUpdated?: () => void;
  wardName?: string;
}

type TabType = 'description' | 'analytics' | 'log' | 'members' | 'forms' | 'comments' | 'equipment';

interface TabConfig {
  type: TabType;
  label: string | React.ReactNode;
  value: number;
}

interface RefreshTrainingOptions {
  silent?: boolean;
  preserveTab?: boolean;
}

function formatTimeRange(startTimeStr: string, totalDurationSeconds?: number): string {
  const start = new Date(startTimeStr);
  const startFormatted = format(start, 'h:mm aaa');
  if (!totalDurationSeconds) return startFormatted;
  const end = addSeconds(start, totalDurationSeconds);
  const endFormatted = format(end, 'h:mm aaa');
  return `${startFormatted} – ${endFormatted}`;
}

const TrainingModalV2: FC<TrainingModalV2Props> = ({
  open,
  onClose,
  trainingId,
  trainingType,
  onTrainingUpdated,
  wardName,
}) => {
  const navigate = useNavigate();
  const trainingsService = useService(TrainingsService);
  const assignedTrainingsService = useService(AssignedTrainingsService);
  const customFormsService = useService(CustomTrainingFormsService);
  const teamsService = useService(TeamsService);
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState<boolean>(false);
  const [training, setTraining] = useState<Training | null>(null);
  const [isTeamTraining, setIsTeamTraining] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [commentsWereMarkedAsRead, setCommentsWereMarkedAsRead] = useState<boolean>(false);
  const [attachedForm, setAttachedForm] = useState<CustomTrainingForm | null>(null);
  const [formLoading, setFormLoading] = useState<boolean>(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [isCoachOfTeam, setIsCoachOfTeam] = useState<boolean>(false);
  const [showTransferModal, setShowTransferModal] = useState<boolean>(false);
  const [editTeamTrainingModalOpen, setEditTeamTrainingModalOpen] = useState<boolean>(false);
  const [teamObject, setTeamObject] = useState<any>(null);

  // Fetch training data when modal opens
  useEffect(() => {
    if (open && trainingId) {
      setCommentsWereMarkedAsRead(false);
      fetchTraining(trainingId, trainingType);
    }
  }, [open, trainingId, trainingType]);

  const closeModal = () => {
    const shouldNotifyParent = commentsWereMarkedAsRead;
    setCommentsWereMarkedAsRead(false);
    onClose();
    if (shouldNotifyParent && onTrainingUpdated) {
      onTrainingUpdated();
    }
  };

  // Function to check if user is a coach of the training's team
  const checkCoachStatus = async (teamId: number) => {
    try {
      const response = await teamsService.getTeams();
      const teams = response.data.data;
      // Check if any team matches the training's team ID and has coach role
      const userTeam = teams.find(
        (team: any) =>
          (team.id && (team.id === teamId || team.id.toString() === teamId.toString())) ||
          (team.teamId && team.teamId === teamId.toString())
      );
      setIsCoachOfTeam(userTeam?.role === 'coach');
    } catch (error) {
      setIsCoachOfTeam(false);
    }
  };

  const fetchTraining = async (
    id: number,
    type: 'athlete' | 'coach' | 'guardian',
    options?: RefreshTrainingOptions
  ) => {
    const silent = options?.silent ?? false;
    const preserveTab = options?.preserveTab ?? false;

    if (!silent) {
      setLoading(true);
    }
    try {
      const response = await trainingsService.getTraining2(id, type);
      const trainingData = response.data.data;
      setTraining(trainingData);
      setIsTeamTraining(!!trainingData?.team);
      if (!preserveTab) {
        setActiveTab(0);
      }

      // Check if user is coach of the team
      if (trainingData?.team?.id) {
        await checkCoachStatus(trainingData.team.id);
        // Convert TrainingTeam to team object format for EditTrainingComponent
        setTeamObject({
          id: trainingData.team.id.toString(),
          name: trainingData.team.name,
        });
      } else {
        setIsCoachOfTeam(false);
        setTeamObject(null);
      }

      // Fetch form details if training has a form attached and user is a coach
      if (type === 'coach' && trainingData?.trainingFormId && trainingData?.team?.id) {
        fetchFormDetails(trainingData.team.id, trainingData.trainingFormId);
      } else {
        setAttachedForm(null);
      }

      if (!silent) {
        setLoading(false);
      }
    } catch (error) {
      if (!silent) {
        setLoading(false);
        closeModal();
      }
      enqueueSnackbar('There was an error while fetching this training. Please try again later.', {
        autoHideDuration: 3000,
        variant: 'error',
        anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
      });
    }
  };

  const refreshTraining = (options?: RefreshTrainingOptions) => {
    if (trainingId) {
      return fetchTraining(trainingId, trainingType, options);
    }
    return Promise.resolve();
  };

  const refreshTrainingInPlace = () => refreshTraining({ silent: true, preserveTab: true });

  // Function to clear unread comments count locally
  const clearUnreadCommentsCount = (clearedCount: number) => {
    if (training) {
      const currentUnreadComments = training.unreadComments || 0;
      const newUnreadComments = Math.max(0, currentUnreadComments - clearedCount);
      setTraining({ ...training, unreadComments: newUnreadComments });
      if (newUnreadComments === 0) {
        setCommentsWereMarkedAsRead(true);
      }
    }
  };

  const clearTeamMemberUnreadComments = () => {
    if (!training?.team?.assignees) return;

    const memberUnreadSum = training.team.assignees.reduce(
      (total, assignee) => total + (assignee.unreadComments || 0),
      0
    );
    const newUnreadComments = Math.max(0, (training.unreadComments || 0) - memberUnreadSum);

    setTraining({
      ...training,
      unreadComments: newUnreadComments,
      team: {
        ...training.team,
        assignees: training.team.assignees.map((assignee) => ({ ...assignee, unreadComments: 0 })),
      },
    });

    if (newUnreadComments === 0) {
      setCommentsWereMarkedAsRead(true);
    }
  };

  // Function to mark training as complete
  const markTrainingComplete = async () => {
    if (!training?.id) return;

    try {
      await trainingsService.completePersonalTraining(training.id.toString());
      enqueueSnackbar('Training marked as complete successfully!', {
        autoHideDuration: 2000,
        variant: 'success',
        anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
      });
      await refreshTrainingInPlace();
    } catch (error) {
      enqueueSnackbar('There was an error marking this training as complete. Please try again later.', {
        autoHideDuration: 3000,
        variant: 'error',
        anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
      });
    }
  };

  // Function to handle RSVP status updates
  const handleRSVP = async (status: 'a' | 'd') => {
    if (!training?.id) return;

    try {
      await assignedTrainingsService.updateRSVPStatus(training.id.toString(), status);
      enqueueSnackbar('RSVP updated successfully!', {
        autoHideDuration: 2000,
        variant: 'success',
        anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
      });
      await refreshTrainingInPlace();
    } catch (error) {
      enqueueSnackbar('There was an error updating your RSVP. Please try again later.', {
        autoHideDuration: 3000,
        variant: 'error',
        anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
      });
    }
  };

  // Function to fetch form details
  const fetchFormDetails = async (teamId: number, formId: number) => {
    setFormLoading(true);
    try {
      const response = await customFormsService.getForm(teamId.toString(), formId);
      setAttachedForm(response.data.data);
      setFormLoading(false);
    } catch (error) {
      setFormLoading(false);
      setAttachedForm(null);
    }
  };

  // Menu handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  // Edit handlers
  const handleEditClick = () => {
    handleMenuClose();
    setEditModalOpen(true);
  };

  const handleEditClose = () => {
    setEditModalOpen(false);
  };

  const handleEditRefresh = async () => {
    await refreshTrainingInPlace();
    if (onTrainingUpdated) {
      onTrainingUpdated();
    }
  };

  // Delete handlers
  const handleDeleteClick = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!training?.id) return;

    setDeleteDialogOpen(false);
    setLoading(true);

    try {
      await trainingsService.deletePersonalTraining(training.id.toString());
      enqueueSnackbar('Training deleted successfully!', {
        autoHideDuration: 2000,
        variant: 'success',
        anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
      });

      if (onTrainingUpdated) {
        onTrainingUpdated();
      }
      onClose();
    } catch (error) {
      setLoading(false);
      enqueueSnackbar('There was an error deleting this training. Please try again later.', {
        autoHideDuration: 3000,
        variant: 'error',
        anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
      });
    }
  };

  // Transfer workout handlers
  const handleTransferClick = () => {
    handleMenuClose();
    setShowTransferModal(true);
  };

  const handleTransferClose = () => {
    setShowTransferModal(false);
    refreshTrainingInPlace();
  };

  // Edit team training handler
  const handleEditTeamTrainingClick = () => {
    handleMenuClose();
    setEditTeamTrainingModalOpen(true);
  };

  const handleEditTeamTrainingClose = () => {
    setEditTeamTrainingModalOpen(false);
  };

  const handleEditTeamTrainingRefresh = async () => {
    await refreshTrainingInPlace();
    if (onTrainingUpdated) {
      onTrainingUpdated();
    }
  };

  // Function to build tab configuration based on training conditions
  const buildTabsConfig = (): TabConfig[] => {
    if (!training) return [];

    const tabs: TabConfig[] = [];
    let currentIndex = 0;

    const hasWorkoutSamples = (training.workoutData?.samples?.length ?? 0) > 0;
    const hasTeam = !!training.team;
    const hasForm = trainingType === 'coach' && !!training.trainingFormId;

    // 1. Description - always first, always default
    tabs.push({ type: 'description', label: 'Description', value: currentIndex++ });

    // 2. Forms - only for coaches with formID
    if (hasForm) {
      tabs.push({ type: 'forms', label: 'Forms', value: currentIndex++ });
    }

    // 3. Analytics or Log - mutually exclusive
    if (hasWorkoutSamples) {
      tabs.push({ type: 'analytics', label: 'Analytics', value: currentIndex++ });
    } else {
      tabs.push({ type: 'log', label: 'Log', value: currentIndex++ });
    }

    // 4. Notes - always show
    const commentsLabel = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <span>Notes</span>
        {(training.unreadComments ?? 0) > 0 && (
          <Typography
            component="span"
            sx={{
              fontSize: '12px',
              fontWeight: 'bold',
              color: palette.success.main,
              backgroundColor: palette.success.light,
              borderRadius: '8px',
              padding: '2px 6px',
              minWidth: '16px',
              textAlign: 'center',
            }}
          >
            {training.unreadComments}
          </Typography>
        )}
      </Box>
    );

    tabs.push({ type: 'comments', label: commentsLabel, value: currentIndex++ });

    // 5. Members - only if team exists
    if (hasTeam) {
      tabs.push({ type: 'members', label: 'Members', value: currentIndex++ });
    }

    // 6. Equipment - always show
    tabs.push({ type: 'equipment', label: 'Equipment', value: currentIndex++ });

    return tabs;
  };

  // Function to render tab content
  const renderTabContent = () => {
    if (!training) return null;

    const tabsConfig = buildTabsConfig();
    const activeTabConfig = tabsConfig.find((tab) => tab.value === activeTab);

    if (!activeTabConfig) return null;

    switch (activeTabConfig.type) {
      case 'description':
        return <DescriptionTab description={training.description || ''} />;

      case 'analytics':
        return <AnalyticsTab workoutData={training.workoutData || null} />;

      case 'log':
        return (
          <LogTab
            training={training}
            trainingType={trainingType}
            onRefresh={refreshTrainingInPlace}
            onEdit={!isTeamTraining ? handleEditClick : isCoachOfTeam ? handleEditTeamTrainingClick : undefined}
          />
        );

      case 'members':
        return (
          <MembersTab
            team={training.team}
            type={trainingType}
            trainingId={training.id?.toString() || ''}
            training={training}
            onRefresh={refreshTrainingInPlace}
            onUnreadCommentsCleared={clearUnreadCommentsCount}
          />
        );

      case 'forms':
        return (
          <FormsTab
            form={attachedForm}
            loading={formLoading}
            teamMembers={training.team?.assignees || []}
            disabled={false}
            trainingId={trainingId}
          />
        );

      case 'comments':
        return (
          <CommentsTab
            trainingId={training.id?.toString() || ''}
            training={training}
            trainingType={trainingType}
            onUnreadCommentsCleared={clearUnreadCommentsCount}
            onTeamUnreadCommentsCleared={clearTeamMemberUnreadComments}
            onEffortRatingUpdated={refreshTrainingInPlace}
            onRefresh={refreshTrainingInPlace}
          />
        );

      case 'equipment':
        return (
          <EquipmentTab
            workoutData={training.workoutData || null}
            trainingId={trainingId}
            trainingType={trainingType}
            onEquipmentLinked={refreshTrainingInPlace}
          />
        );

      default:
        return null;
    }
  };

  const renderTrainingBody = (trainingData: Training) => {
    const isComplete = trainingData.workoutData?.complete ?? false;
    const hasSamples = (trainingData.workoutData?.samples?.length ?? 0) > 0;
    const rsvpStatus = trainingData.workoutData?.rsvp;

    return (
      <>
        {/* Training title + status chip */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            px: 2.5,
            pt: 2,
            mb: 0.5,
          }}
        >
          <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.3, maxWidth: '75%' }}>
            {trainingData.title}
          </Typography>
          <Chip
            label={isComplete ? 'Completed' : 'Planned'}
            size="small"
            sx={{
              bgcolor: isComplete ? palette.success.light : palette.primary.light,
              color: isComplete ? palette.success.dark : palette.primary.main,
              fontWeight: 600,
              fontSize: '12px',
              borderRadius: '8px',
              mt: 0.25,
            }}
          />
        </Box>

        {/* Location & time */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.5, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {[
              trainingData.location,
              trainingData.time ? formatTimeRange(trainingData.time, trainingData.totalDurationPlanned) : 'Time TBD',
            ]
              .filter(Boolean)
              .join(' • ')}
          </Typography>
          {trainingData.actualStartTime && (
            <Typography variant="body2" color="primary">
              {format(new Date(trainingData.actualStartTime), 'h:mm aaa')}
            </Typography>
          )}
          {hasSamples && <WatchIcon color="primary" fontSize="small" />}
        </Box>

        {/* Stats / Map summary */}
        <TrainingModalSummary training={trainingData} />

        {/* Sport / Type / Focus chips + RSVP attending */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
            px: 2.5,
            mb: 2,
          }}
        >
          {/* Sport / Type / Focus chips */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {trainingData.sport && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  bgcolor: palette.grey[100],
                  borderRadius: '10px',
                  px: 1.5,
                  py: 0.75,
                  minWidth: 60,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, lineHeight: 1.2 }}
                >
                  Sport
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  {trainingData.sport}
                </Typography>
              </Box>
            )}
            {trainingData.sportType && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  bgcolor: palette.grey[100],
                  borderRadius: '10px',
                  px: 1.5,
                  py: 0.75,
                  minWidth: 60,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, lineHeight: 1.2 }}
                >
                  Type
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  {trainingData.sportType}
                </Typography>
              </Box>
            )}
            {trainingData.focus && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  bgcolor: palette.grey[100],
                  borderRadius: '10px',
                  px: 1.5,
                  py: 0.75,
                  minWidth: 60,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, lineHeight: 1.2 }}
                >
                  Focus
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  {trainingData.focus}
                </Typography>
                {trainingData.focus === 'Race' && (
                  <Link
                    component="button"
                    variant="caption"
                    underline="hover"
                    color={palette.primary.main}
                    onClick={() => {
                      if (trainingData.team) {
                        navigate(`/teams/${trainingData.team.id}/race-log`);
                        return;
                      }
                      const equipmentId = trainingData.workoutData?.equipment?.id;
                      if (equipmentId != null) {
                        const params = new URLSearchParams({
                          q: '',
                          page: '1',
                          per_page: '10',
                          equipment_id: String(equipmentId),
                          equipment_tab: 'race-log',
                        });
                        navigate(`/equipment?${params.toString()}`);
                      } else {
                        navigate(`/equipment?q=&page=1&per_page=10`);
                      }
                    }}
                  >
                    View Race Log
                  </Link>
                )}
              </Box>
            )}
          </Box>

          {/* I am attending — Yes / No (planned state only) */}
          {!isComplete && trainingType !== 'guardian' && trainingData.team && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                I am attending
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant={rsvpStatus === 'a' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => handleRSVP('a')}
                  disabled={loading}
                  sx={{
                    borderRadius: '20px',
                    textTransform: 'none',
                    minWidth: 52,
                    fontWeight: 600,
                    ...(rsvpStatus !== 'a' && {
                      borderColor: palette.grey[300],
                      color: palette.text.secondary,
                    }),
                  }}
                >
                  Yes
                </Button>
                <Button
                  variant={rsvpStatus === 'd' ? 'contained' : 'outlined'}
                  size="small"
                  color={rsvpStatus === 'd' ? 'error' : 'inherit'}
                  onClick={() => handleRSVP('d')}
                  disabled={loading}
                  sx={{
                    borderRadius: '20px',
                    textTransform: 'none',
                    minWidth: 52,
                    fontWeight: 600,
                    ...(rsvpStatus !== 'd' && {
                      borderColor: palette.grey[300],
                      color: palette.text.secondary,
                    }),
                  }}
                >
                  No
                </Button>
              </Box>
            </Box>
          )}
        </Box>

        {/* Tabs navigation */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant="fullWidth"
            sx={{
              minHeight: 48,
              '.MuiTab-root': {
                fontSize: 14,
                fontWeight: 500,
                textTransform: 'none',
                color: palette.text.secondary,
              },
              '.Mui-selected': {
                color: palette.primary.main,
                fontWeight: 600,
              },
              '.MuiTabs-indicator': {
                backgroundColor: palette.primary.main,
                height: 3,
              },
            }}
          >
            {buildTabsConfig().map((tab) => (
              <Tab key={tab.value} label={tab.label} value={tab.value} />
            ))}
          </Tabs>
        </Box>

        {/* Tab content */}
        <Box sx={{ backgroundColor: palette.background.subtle }}>{renderTabContent()}</Box>
      </>
    );
  };

  return (
    <>
      <Modal
        open={open}
        onClose={closeModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          onClick: closeModal,
        }}
        disableEscapeKeyDown={false}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Fade in={open} timeout={500}>
          <Paper
            sx={{
              width: '800px',
              height: '90vh',
              backgroundColor: palette.background.paper,
              borderRadius: '25px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              position: 'relative',
              outline: 'none',
              transform: 'scale(1)',
              transition: 'transform 0.3s ease-in-out',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* More options button is now rendered inside ModalHeader via menuButton prop */}

            {loading ? (
              /* Loading State */
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CircularProgress size={60} />
              </Box>
            ) : training ? (
              /* Training Data Loaded */
              <>
                {/* Sticky top bar — always visible */}
                <TrainingModalHeader
                  training={training}
                  trainingType={trainingType}
                  isLoading={loading}
                  onClose={closeModal}
                  wardName={wardName}
                  markTrainingComplete={markTrainingComplete}
                  menuButton={(() => {
                    if (trainingType === 'guardian') return null;
                    const showDelete = !isTeamTraining;
                    const showEdit = !isTeamTraining;
                    const showTransferWorkout = (training?.workoutData?.samples?.length ?? 0) > 0;
                    const showEditTeamTraining = isTeamTraining && isCoachOfTeam;
                    const showMenu = showDelete || showEdit || showTransferWorkout || showEditTeamTraining;
                    return showMenu ? (
                      <IconButton onClick={handleMenuOpen} aria-label="More options" size="small">
                        <MoreVertIcon />
                      </IconButton>
                    ) : null;
                  })()}
                />

                {/* Scrollable body */}
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                  }}
                >
                  {renderTrainingBody(training)}
                </Box>
              </>
            ) : (
              /* Fallback - should not normally be seen */
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box sx={{ textAlign: 'center', color: palette.text.secondary }}>No training data available</Box>
              </Box>
            )}
          </Paper>
        </Fade>
      </Modal>

      {/* Options Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {/* Edit option - only for personal trainings */}
        {!isTeamTraining && (
          <MenuItem onClick={handleEditClick} sx={{ gap: 1 }}>
            <EditIcon fontSize="small" />
            Edit Training
          </MenuItem>
        )}

        {/* Delete option - only for personal trainings */}
        {!isTeamTraining && (
          <MenuItem onClick={handleDeleteClick} sx={{ color: palette.error.dark, gap: 1 }}>
            <DeleteIcon fontSize="small" />
            Delete Training
          </MenuItem>
        )}

        {/* Transfer workout option - only if workout samples exist */}
        {(training?.workoutData?.samples?.length ?? 0) > 0 && (
          <MenuItem onClick={handleTransferClick} sx={{ gap: 1 }}>
            <SwapCallsIcon fontSize="small" />
            Transfer Workout
          </MenuItem>
        )}

        {/* Edit team training option - only if user is coach of the team */}
        {isTeamTraining && isCoachOfTeam && (
          <MenuItem onClick={handleEditTeamTrainingClick} sx={{ gap: 1 }}>
            <EditIcon fontSize="small" />
            Edit Team Training
          </MenuItem>
        )}
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Training?</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this training? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Training Modal */}
      <COEEditPersonalTrainingModal
        open={editModalOpen}
        onClose={handleEditClose}
        handleRefresh={handleEditRefresh}
        training={training}
      />

      {/* Transfer Workout Modal */}
      {training && (
        <TransferWorkoutModal
          open={showTransferModal}
          onClose={handleTransferClose}
          trainingId={training.id ? parseInt(training.id) : 0}
          trainingTime={training.time}
          trainingTitle={training.title}
        />
      )}

      {/* Edit Team Training Modal */}
      {editTeamTrainingModalOpen && training && teamObject && (
        <EditTrainingComponent
          open={editTeamTrainingModalOpen}
          handleClose={handleEditTeamTrainingClose}
          handleRefresh={handleEditTeamTrainingRefresh}
          team={teamObject}
          week={dayjs(training.time)}
          trainingId={training.id?.toString()}
        />
      )}
    </>
  );
};

export default TrainingModalV2;
